import React from "react";

import { Alert, Link } from "@zapier/design-system";
import { useTrackingContext } from "@zapier/toolbox-tracking";
import { extractParams } from "@zapier/url-utils";
import { Currency, defaultCurrency } from "@zapier/currency";

import { useCurrentAccountId } from "@zapier/payment/esm/hooks/useCurrentAccountId";
import { useCurrencyRuleset } from "@zapier/payment/esm/hooks/useCurrencyRuleset";
import {
  ApolloInjection,
  ApiKeys,
  PaymentSourceType,
} from "@zapier/payment/esm/types";
import { ApolloInjectionProvider } from "@zapier/payment/esm/contexts/ApolloInjection";

import usePaymentSourceState from "./hooks/usePaymentSourceState";
import { PaymentMethodsSection } from "./PaymentMethodsSection/PaymentMethodsSection";
import { Skeleton } from "./Skeleton";
import { apiKeys as defaultApiKeys } from "./apiKeys";

type Props = {
  accountId?: number;
  apiKeys?: ApiKeys;
  autoEdit?: boolean;
  editInModal?: boolean;
  onSaveSuccess?: () => void;
  selectedCurrency?: Currency;
} & ApolloInjection;

export function PaymentApp({
  accountId,
  apiKeys,
  autoEdit,
  editInModal,
  onSaveSuccess,
  selectedCurrency = defaultCurrency,
  ...apolloInjection
}: Props) {
  const { trackEvent } = useTrackingContext();
  const currentAccount = useCurrentAccountId(apolloInjection);

  const startEdit = Boolean(
    extractParams(window.location.href)?.section === PaymentSourceType.card ||
      autoEdit
  );

  const {
    currentSource,
    error,
    forceCardUpdate,
    isEditing,
    isLoading: isLoadingPaymentSource,
    isSaving,
    method,
    onCancelEdit,
    onEdit,
    onError,
    onHideSuccessMessage,
    onSave,
    onSelectMethod,
    showSuccessMessage,
    supportedMethods,
  } = usePaymentSourceState({
    accountId: accountId || currentAccount.accountId,
    onSaveSuccess,
    onTrack: trackEvent,
    startEdit,
    ...apolloInjection,
  });

  const {
    currencyRuleset,
    isLoading: isLoadingCurrencyRuleset,
    error: currencyRulesetError,
  } = useCurrencyRuleset({
    accountId,
    ...apolloInjection,
  });
  const isLoading = isLoadingPaymentSource || isLoadingCurrencyRuleset;

  if (isLoading) {
    return <Skeleton />;
  }

  if (currencyRulesetError) {
    return (
      <Alert title="Error:" type="error">
        There is a problem loading the payment form.{" "}
        <Link onClick={() => window.location.reload()} role="link">
          Reload the page
        </Link>{" "}
        to try again.
      </Alert>
    );
  }

  return (
    <ApolloInjectionProvider {...apolloInjection}>
      <PaymentMethodsSection
        accountId={accountId || currentAccount.accountId}
        apiKeys={{
          braintree: (apiKeys || defaultApiKeys).braintreePubKey as string,
          stripe: (apiKeys || defaultApiKeys).stripePubKey as string,
        }}
        currencyRuleset={currencyRuleset}
        currentMethod={method}
        editInModal={Boolean(editInModal)}
        error={error}
        forceCardUpdate={forceCardUpdate}
        hasPaymentSource={Boolean(currentSource)}
        isEditing={isEditing}
        isEditingInFlight={isSaving}
        onCancelEdit={onCancelEdit}
        onEdit={onEdit}
        onErrorEdit={onError}
        onHideSuccessMessage={onHideSuccessMessage}
        onSaveEdit={onSave}
        onSelectSource={onSelectMethod}
        savedSource={currentSource}
        selectedCurrency={selectedCurrency}
        showChangeSuccessMessage={showSuccessMessage}
        supportedMethods={supportedMethods}
      />
    </ApolloInjectionProvider>
  );
}
